// Подтверждение списания. SMY-71 · экран 04
//
// Единственное, что пережило переезд в ленту нетронутым: это и было окном
// поверх, а не экраном. Весь текст собран сервером и приехал вместе с
// распознаванием — метка, обещание, объяснение «почему спрашиваю», состав
// результата, числа и подписи кнопок. Собирать его здесь значило бы держать
// вторую версию обещания, которая разойдётся с настройками из админки.
//
// После слияния лимитов (06.09.2026) шит стал редким: вопрос, поиск и
// проверка списываются той же единицей, что и обычный ответ, а обычный ответ
// не подтверждают. Остались документы и изображения — то, что человек и
// правда выбирает отдельно.
//
// Три правила, которые окно обязано соблюсти, и все три из SMY-60:
//
//   1. Отмена до старта не списывает ничего. Esc, крестик и клик по фону —
//      это отмена, а не «свернуть».
//   2. Чекбокс запоминает решение ДЛЯ ЭТОЙ ЕДИНИЦЫ.
//   3. Настройка сохраняется, только если человек действительно запустил.
//
// Отсюда же уходит единственное событие, о котором клиент рассказывает
// серверу сам (SMY-82). Сервер видит, что окно показано, — шит собирает он;
// чем оно кончилось, он не видит ничем: отмена это уход с экрана. Правило
// «что считать чем» живёт в ядре (`confirmDecisionOf`), потому что окон
// будет три — веб, iOS, Android, — а решение одно.

import { useState } from 'react'
import { formatUnit, type ScenarioId } from '@smyslika/core'
import { OfflineError, type ConfirmationSheet, type Intent } from '@smyslika/api-client'

import { useSession } from '../session.tsx'
import { Button, Card, Checkbox, Icon, ProgressBar, Sheet } from '../ui/index.ts'

/** Иконка сценария рядом с меткой. */
const SCENARIO_ICONS: Record<ScenarioId, Parameters<typeof Icon>[0]['name']> = {
  chat: 'write',
  write: 'write',
  doc_review: 'docReview',
  doc_create: 'docCreate',
  image: 'image',
}

export function ConfirmSheet({
  intent,
  onCancel,
  onRun,
}: {
  intent: Intent
  onCancel: () => void
  onRun: (scenario: ScenarioId) => Promise<void>
}) {
  const { confirmations, events } = useSession()
  const [skip, setSkip] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const sheet: ConfirmationSheet | undefined = intent.confirm
  if (sheet == null) return null

  async function run(scenario: ScenarioId): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      // Настройка сохраняется до запуска, но только при согласии: иначе
      // человек, передумавший на этом же экране, всё равно перестал бы
      // получать вопрос. Отказ сохранить её не должен рушить запуск —
      // человек просил выполнить задачу, а не запомнить предпочтение. И не
      // ожидается: повисшее сохранение держало бы окно и запуск бессрочно —
      // срока у него нет, а запуску оно не нужно (ворота ревью 5, SMY-449).
      if (skip && sheet != null) void confirmations.set(sheet.unit, true).catch(() => undefined)
      // Записывается ДО запуска и не ожидается: решение человек уже принял, а
      // упавший запуск не делает его непринятым. Ждать здесь значило бы
      // задержать работу ради отчёта.
      if (sheet != null) {
        events.confirmDecided({
          ran: true,
          skipAsked: skip,
          declined: scenario !== sheet.scenario,
          unit: sheet.unit,
          scenario: sheet.scenario,
        })
      }
      await onRun(scenario)
    } catch (e) {
      setError(e instanceof OfflineError ? 'Нет связи. Проверь интернет.' : (e as Error).message)
      setBusy(false)
    }
  }

  /**
   * Уход с экрана. Esc, крестик и клик по фону — всё сюда, и всё это отмена.
   *
   * Единственный способ узнать про неё: запуска не будет, строки в `runs` не
   * появится, и вывести отмену из молчания нельзя — так же выглядит человек,
   * который отвлёкся и вернулся.
   */
  function cancel(): void {
    // Старт уже ушёл — закрыть окно значило бы записать отмену задачи, которая
    // через миг появится карточкой хода работы и при успехе спишется
    // (SMY-352: окно с 05.10.2026 открыто, пока идёт старт).
    if (busy) return
    if (sheet != null) {
      events.confirmDecided({
        ran: false,
        skipAsked: skip,
        declined: false,
        unit: sheet.unit,
        scenario: sheet.scenario,
      })
    }
    onCancel()
  }

  const spent = sheet.remainingBefore - sheet.remainingAfter

  return (
    <Sheet open onClose={cancel} closable={!busy} label={sheet.promise}>
      <div className="confirm__label">
        <Icon name={SCENARIO_ICONS[sheet.scenario]} size={20} strokeWidth={1.9} />
        <span>{sheet.label}</span>
      </div>

      <div className="confirm__head">
        <h2 className="confirm__title">{sheet.promise}</h2>
        <p className="confirm__explain">{sheet.explain}</p>
      </div>

      <ul className="confirm__list">
        {sheet.includes.map((line) => (
          <li key={line} className="outcome__item">
            <span className="outcome__tick">
              <Icon name="tick" size={17} strokeWidth={2.4} />
            </span>
            {line}
          </li>
        ))}
      </ul>

      <Card kind="nested" className="confirm__cost">
        <div className="confirm__cost-row">
          <span className="confirm__cost-cell">
            <span className="outcome__caption">Спишется с лимита</span>
            <span className="confirm__cost-value">{formatUnit(spent, sheet.unit)}</span>
          </span>

          {sheet.etaSeconds != null ? (
            <span className="confirm__eta">
              <Icon name="time" size={15} strokeWidth={2} />
              Примерно {sheet.etaSeconds} секунд
            </span>
          ) : null}

          <span className="confirm__cost-cell confirm__cost-cell--right">
            <span className="outcome__caption">Останется</span>
            <span className="confirm__cost-value confirm__cost-value--accent">
              {sheet.remainingAfter} из {sheet.limit}
            </span>
          </span>
        </div>
        <ProgressBar value={sheet.remainingAfter} max={sheet.limit} />
      </Card>

      {error != null ? (
        <p className="error-line" role="alert">
          {error}
        </p>
      ) : null}

      <div className="confirm__actions">
        <Button disabled={busy} onClick={() => void run(sheet.scenario)}>
          {sheet.action}
        </Button>
        {/* Отказ равной ширины с основным действием: спрятанный бесплатный
            выход — это не выход. Появляется только когда обычный ответ вообще
            есть, и это решает сервер. */}
        {sheet.decline != null ? (
          <Button kind="ghost" disabled={busy} onClick={() => void run('chat')}>
            {sheet.decline}
          </Button>
        ) : null}
      </div>

      <div className="confirm__foot">
        <Checkbox checked={skip} onChange={setSkip}>
          {sheet.skipLabel}
        </Checkbox>
        <span className="confirm__notice">{sheet.notice}</span>
      </div>
    </Sheet>
  )
}
